import Header from 'components/molecules/Header/Header'
import AlertDialog from 'components/AlertDialog/AlertDialog'
import { useReferringPhysiciansLogic } from './ReferringPhysicians.logic'
import { Box, Button, Checkbox, TableRowProps, TextField } from '@mui/material'
import { DataTable } from 'components/molecules/DataTable/DataTable'
import { LoadingButton } from 'components/mui'
import { Delete, ReadMore, Email } from '@mui/icons-material'
import { useIsMobile } from 'lib/utils/mediaQueries'
import styles from './ReferringPhysicians.module.scss'
import { formatUSPhoneNumberForDisplay } from 'common/utils'
import { FC, useState } from 'react'
import SmsIcon from '@mui/icons-material/Sms'
import { CustomModal } from 'components/molecules/Modal/Modal'
import ImportDialog from 'components/molecules/Modal/ImportDialog'
import ImportResultsModal from './ImportResultsModal/ImportResultsModal'
import { useImportPhysicians } from 'lib/services/api/referring-physicians/import-physicians'
import { ImportPhysiciansResponse } from 'lib/services/api/referring-physicians/types'
import { convertToCSVIfNeeded } from 'lib/utils/excelToCsv'
import { AxiosError } from 'axios'
import { enqueueSnackbar } from 'notistack'

const ReferringPhysicians: FC = () => {
  const {
    enableSms,
    selectAll,
    smsLoading,
    physicians,
    searchLabel,
    isAlertOpen,
    smsModal,
    searchString,
    physiciansTitle,
    isSendingBulkEmail,
    filteredPhysicians,
    isLoadingPhysicians,
    showPhysiciansMobile,
    showPhysiciansSpeciality,
    errorGettingPhysiciansData,
    referringPhysiciansHeadCells,
    isDeletingReferringPhysician,
    sendEmail,
    handleFocus,
    handleSearch,
    sendBulkEmail,
    handleSendSms,
    openAlertDialog,
    setSmsModal,
    closeAlertDialog,
    handleBulkSelection,
    setIsSendingBulkEmail,
    handleCheckPhysicianClick,
    navigateToReferringPhysician,
    handleCreateReferringPhysician,
    handleDeleteReferringPhysician,
    refetchPhysiciansData,
  } = useReferringPhysiciansLogic()

  const [importDialogOpen, setImportDialogOpen] = useState(false)
  const [importFile, setImportFile] = useState<File | null>(null)
  const [importError, setImportError] = useState<string | null>(null)
  const [importResults, setImportResults] =
    useState<ImportPhysiciansResponse | null>(null)
  const [importResultsModalOpen, setImportResultsModalOpen] = useState(false)

  const { mutateAsync: importPhysicians, isLoading: importLoading } =
    useImportPhysicians()

  const handleOpenImportDialog = () => setImportDialogOpen(true)
  const handleCloseImportDialog = () => {
    setImportDialogOpen(false)
    setImportFile(null)
    setImportError(null)
  }

  const handleFileSelect = (file: File | null) => {
    setImportFile(file)
    setImportError(null)
  }

  const handleImportSubmit = async () => {
    if (!importFile) return

    setImportError(null)

    try {
      // Convert Excel to CSV if needed
      const processedFile = await convertToCSVIfNeeded(importFile)

      const data = await importPhysicians({ file: processedFile })

      // Store the results and show the modal
      setImportResults(data)
      setImportResultsModalOpen(true)

      // Refresh the physicians list
      refetchPhysiciansData()

      // Close the import dialog
      setImportDialogOpen(false)
      setImportFile(null)
    } catch (error) {
      console.error('Error in handleImportSubmit:', error)
      const errorMessage =
        error instanceof AxiosError
          ? error.response?.data?.detail ||
            error.message ||
            'An error occurred during upload.'
          : 'An error occurred during upload.'

      setImportError(errorMessage)
      enqueueSnackbar(
        'Failed to upload file. Please make sure that the file is a valid Excel or CSV file and that it matches the expected format.',
        { variant: 'error' }
      )
    }
  }

  const handleCloseImportResultsModal = () => {
    setImportResultsModalOpen(false)
    setImportResults(null)
  }

  const SmsModal = () => {
    return (
      <CustomModal
        open={smsModal.show}
        secondaryButtonAction={() =>
          setSmsModal({ show: false, to: undefined, sms: null })
        }
        primaryButtonText="Send"
        primaryButtonDisabled={smsLoading || !smsModal.sms}
        header="Enter your sms message"
        isLoadingState={smsLoading}
        handleOnSubmit={handleSendSms}
      >
        <TextField
          rows={5}
          multiline
          value={smsModal.sms}
          className={styles.smsTextField}
          onChange={(e) => {
            setSmsModal((prev) => ({ ...prev, sms: e.target.value }))
          }}
        />
      </CustomModal>
    )
  }

  const physiciansRows = filteredPhysicians
    ?.map((physician) => {
      if (!physician._id) return null

      const formattedLastName = physician.lastName
        ? physician.lastName
            .split(' ')
            .map(
              (word) =>
                word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
            )
            .join(' ')
        : '-'

      const formattedFirstName = physician.firstName
        ? physician.firstName
            .split(' ')
            .map(
              (word) =>
                word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
            )
            .join(' ')
        : '-'

      const row: {
        lastName: string
        firstName: string
        speciality?: String
        email: string
        mobile?: string
        actions?: JSX.Element
        sendEmail?: JSX.Element
      } = {
        lastName: formattedLastName,
        firstName: formattedFirstName,
        ...(showPhysiciansSpeciality && {
          speciality: physician.speciality || '-',
        }),
        email: physician.email,
        ...(showPhysiciansMobile && {
          mobile: physician.mobile
            ? formatUSPhoneNumberForDisplay(physician.mobile)
            : '-',
        }),
      }

      isSendingBulkEmail
        ? (row.sendEmail = (
            <div
              className={`${styles.actionsReferringPhysicians} ${styles.checkbox}`}
            >
              <Checkbox
                id={physician._id}
                checked={
                  filteredPhysicians.find((item) => item._id === physician._id)
                    ?.checked
                }
                onChange={handleCheckPhysicianClick}
              />
            </div>
          ))
        : (row.actions = (
            <div className={styles.actionsReferringPhysicians}>
              {enableSms && (
                <Button
                  onMouseUp={() =>
                    setSmsModal({
                      show: true,
                      to: physician.mobile,
                      sms: `Dr. ${physician.lastName
                        .split(' ')
                        .map(
                          (word) =>
                            word.charAt(0).toUpperCase() +
                            word.slice(1).toLowerCase()
                        )
                        .join(' ')},\n`,
                    })
                  }
                  disabled={!physician.mobile}
                >
                  <SmsIcon />
                </Button>
              )}
              <Button onMouseUp={sendEmail(physician._id)}>
                <Email />
              </Button>
              <Button
                onMouseUp={navigateToReferringPhysician({
                  edit: true,
                  _id: physician._id,
                  email: physician.email,
                  lastName: physician.lastName,
                  firstName: physician.firstName,
                  speciality: physician.speciality,
                  mobile: physician.mobile
                    ? formatUSPhoneNumberForDisplay(physician.mobile)
                    : undefined,
                })}
              >
                <ReadMore />
              </Button>
              <Button onMouseUp={openAlertDialog(physician._id)}>
                <Delete color="error" />
              </Button>
            </div>
          ))

      return row
    })
    .filter(Boolean) as TableRowProps[]

  isSendingBulkEmail
    ? referringPhysiciansHeadCells.push({
        id: 'sendEmail',
        label: (
          <div className={styles.checkboxHeaderReferringPhysicians}>
            <Checkbox
              id="selectAll"
              onChange={handleCheckPhysicianClick}
              checked={selectAll}
            />
          </div>
        ),
        sortable: false,
        numeric: false,
        sx: {
          textAlign: 'right',
        },
      })
    : referringPhysiciansHeadCells.push({
        id: 'actions',
        label: 'Actions',
        numeric: false,
        sx: {
          textAlign: 'right',
        },
      })

  return (
    <div className={styles.referringPhysiciansMainContainer}>
      {SmsModal()}
      <Header
        isMobile={useIsMobile()}
        parent="Reports"
        title={physiciansTitle}
        hasBack={isSendingBulkEmail}
        onBackClick={() => setIsSendingBulkEmail(false)}
      />

      <div className={styles.content}>
        {physicians.length > 0 || isLoadingPhysicians ? (
          <>
            <div
              className={`${styles.inputContainer} ${
                isSendingBulkEmail && styles.flexEnd
              }`}
            >
              <div
                className={`${styles.searchAdd} ${
                  isSendingBulkEmail && styles.hide
                }`}
              >
                <TextField
                  className={styles.input}
                  label={searchLabel}
                  value={searchString}
                  onChange={handleSearch}
                  onFocus={handleFocus(true)}
                  onBlur={handleFocus(false)}
                  size="small"
                />
                <div className={styles.buttonGroup}>
                  <LoadingButton
                    className={styles.addPhysicianButton}
                    variant="outlined"
                    size="large"
                    onClick={handleCreateReferringPhysician}
                  >
                    Add Physician
                  </LoadingButton>
                  <LoadingButton
                    className={styles.importPhysiciansButton}
                    variant="outlined"
                    size="large"
                    onClick={handleOpenImportDialog}
                  >
                    Import
                  </LoadingButton>
                  <LoadingButton
                    className={styles.sendEmailButton}
                    variant="outlined"
                    size="large"
                    onClick={
                      isSendingBulkEmail ? sendBulkEmail : handleBulkSelection
                    }
                    endIcon={<Email />}
                  >
                    Send All
                  </LoadingButton>
                </div>
              </div>
            </div>

            <Box sx={{ px: 2 }}>
              <DataTable
                tableRows={physiciansRows}
                isLoading={isLoadingPhysicians}
                isErrored={!!errorGettingPhysiciansData}
                tableHeaders={referringPhysiciansHeadCells}
                errorMessage={`Error getting ${physiciansTitle.toLocaleLowerCase()}...`}
                enableVerticalScroll
                maxHeight="72vh"
              />
            </Box>
          </>
        ) : (
          <div className={styles.noData}>
            <h3>{`No ${physiciansTitle.toLocaleLowerCase()} found`}</h3>
            <div>
              <LoadingButton
                variant="contained"
                size="large"
                onClick={handleCreateReferringPhysician}
              >
                Add Physician
              </LoadingButton>
            </div>
          </div>
        )}
      </div>
      <AlertDialog
        zIndex={1000}
        mode="warning"
        title="Delete Confirmation"
        primaryButtonText="Delete"
        isOpen={isAlertOpen.isOpen}
        description={isAlertOpen.description}
        secondaryButtonAction={closeAlertDialog}
        isPrimaryLoading={isDeletingReferringPhysician}
        primaryButtonAction={handleDeleteReferringPhysician}
      />
      <ImportDialog
        open={importDialogOpen}
        onClose={handleCloseImportDialog}
        onFileSelect={handleFileSelect}
        onSubmit={handleImportSubmit}
        selectedFile={importFile}
        loading={importLoading}
        error={importError}
        title="Import Referring Physicians"
        description="Drag & Drop Excel or CSV file here, or browse to select."
      />
      {importResults && (
        <ImportResultsModal
          open={importResultsModalOpen}
          onClose={handleCloseImportResultsModal}
          results={importResults}
          onPhysicianAdded={refetchPhysiciansData}
        />
      )}
    </div>
  )
}

export default ReferringPhysicians
